import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box,
  alpha,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import NavbarTools from './NavbarTools';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(12px)',
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  margin: '0 8px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.active': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

const Header: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const menuItems = [
    { label: 'Dekyc Home', path: '/' },
    { label: 'Dekyc List', path: '/assets' },
    { label: 'MyDekyc', path: '/mint' },  
    { label: 'About Dekyc', path: '/about' },
  ];

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileNavigation = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  return (
    <StyledAppBar position="static" elevation={0}>
      <Toolbar
        sx={{
          minHeight: {
            xs: 56,
            sm: 64,
          },
          px: {
            xs: 2,
            sm: 3,
          },
          justifyContent: 'space-between',
        }}
      >
        {/* 左侧：汉堡包菜单（移动端）和标题 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* 汉堡包菜单按钮 - 仅在小屏幕显示 */}
          {isSmallScreen && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleMobileMenuToggle}
              sx={{
                mr: 1,
                color: theme.palette.text.primary,
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography 
            variant={isSmallScreen ? "h6" : "h5"}
            component="div"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 700,
              fontSize: {
                xs: '1.1rem',
                sm: '1.25rem',
                md: '1.5rem',
              },
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              {isSmallScreen ? 'Dekyc' : 'Dekyc Platform'}
            </Link>
          </Typography>
          
          {/* 导航按钮 - 仅在非小屏幕显示 */}
          {!isSmallScreen && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <NavButton
                onClick={() => navigate('/')}
                className={isActive('/') ? 'active' : ''}
              >
                Dekyc Home
              </NavButton>
              
              <NavButton
                onClick={() => navigate('/assets')}
                className={isActive('/assets') ? 'active' : ''}
              >
                Dekyc List
              </NavButton>
              
              <NavButton
                onClick={() => navigate('/mint')}
                className={isActive('/mint') ? 'active' : ''}
              >
                MyDekyc
              </NavButton>
              
              <NavButton
                onClick={() => navigate('/about')}
                className={isActive('/about') ? 'active' : ''}
              >
                About Dekyc
              </NavButton>
            </Box>
          )}
        </Box>
        
        {/* 右侧：工具栏 */}
        <NavbarTools 
          showNotifications={false}
          showSettings={false}
          showThemeToggle={true}
          showWallet={true}
        />
        
        {/* 移动端抽屉菜单 */}
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              backgroundColor: theme.palette.background.paper,
            },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Dekyc Platform
            </Typography>
            <IconButton onClick={handleMobileMenuClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Divider />
          
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleMobileNavigation(item.path)}
                  selected={isActive(item.path)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      },
                    },
                  }}
                >
                  <ListItemText 
                    primary={item.label}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: isActive(item.path) ? 600 : 400,
                        color: isActive(item.path) ? theme.palette.primary.main : theme.palette.text.primary,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;